import React from 'react';
import { cardText } from 'components/ExportDesign/exportDesignResultCardData';
import {
  LicensingCardPayload,
  licensingSubOf,
  licensingTitleOf,
  VERDICT_PILLS,
} from 'components/ExportDesign/exportDesignLicensingCardData';

// --- The licensing answer as a card (UX S12 R-13, board 14): the country and the answer first, what it rests
// on and who licenses it, why (the model's grounded reasons), what was checked (deterministic checks only),
// the exceptions as a quiet note, and the full reasoning folded into "How we got here". Rendered from
// `data.licensing_card` in place of the markdown answer, which stays the fallback.

const REGIME_CLASS: Record<string, string> = {
  ITAR: 'regime-itar',
  'EAR-listed': 'regime-ear-listed',
  EAR99: 'regime-ear99',
  uncertain: 'regime-uncertain',
};

export interface ExportDesignLicensingCardProps {
  card: LicensingCardPayload;
}

const ExportDesignLicensingCard: React.FC<ExportDesignLicensingCardProps> = ({ card }) => {
  const pill = VERDICT_PILLS[card.verdict];
  const regimeClass = REGIME_CLASS[card.regime] || 'regime-uncertain';
  const why = card.why.map((reason) => cardText(reason)).filter(Boolean);
  const warnings = card.warnings.map((warning) => cardText(warning)).filter(Boolean);
  const exceptions = cardText(card.exceptions_note);
  const explanation = cardText(card.explanation);

  return (
    <div className='export-design-result'>
      <article className='export-design-block' aria-label='Licensing result'>
        <div className='export-design-block-h'>
          <div>
            <div className='export-design-block-kicker'>Licensing check</div>
            <h3 className='export-design-block-title'>{licensingTitleOf(card)}</h3>
            <div className='export-design-block-sub'>{licensingSubOf(card)}</div>
          </div>
          <div className='export-design-block-pills'>
            <span className={`export-design-pill ${pill.className}`}>{pill.label}</span>
            <span className={`export-design-pill ${regimeClass}`}>
              {card.regime === 'uncertain' ? 'Uncertain' : card.regime}
            </span>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className='export-design-warn'>
            {warnings.map((warning) => (
              <div key={warning}>
                <b>{warning}</b>
              </div>
            ))}
          </div>
        )}

        {why.length > 0 && (
          <section>
            <div className='export-design-section-h'>Why</div>
            <div className='export-design-inset'>
              <ul className='export-design-lic-why'>
                {why.map((reason, index) => (
                  <li key={`${index}-${reason}`}>{reason}</li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {card.checks.length > 0 && (
          <section>
            <div className='export-design-section-h'>What was checked</div>
            <dl className='export-design-lic-checks'>
              {card.checks.map((check) => (
                <React.Fragment key={check.label}>
                  <dt>{cardText(check.label)}</dt>
                  <dd>{cardText(check.value)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </section>
        )}

        {exceptions && (
          <div className='export-design-inset export-design-inset-text'>
            <b>Licence exceptions and other checks:</b> {exceptions}
          </div>
        )}

        {explanation && (
          <details className='export-design-more'>
            <summary>How we got here</summary>
            <div className='export-design-inset export-design-inset-text export-design-lic-explanation'>
              {explanation}
            </div>
          </details>
        )}
      </article>
    </div>
  );
};

export default ExportDesignLicensingCard;
